import { ref } from 'vue'
import * as signalR from '@microsoft/signalr'
import { WOOTDASH_BASE, resolveToken } from '@/api/wootdash'
import type { ConversationListItem, ConversationMessage } from '@/services/wootdashConversations'

// Fase 3 — substitui useChatwootRealtime.ts (ActionCable) pro núcleo de atendimento novo.
// Mesma forma: singleton de módulo, start()/stop() idempotentes, pub/sub via Set<Listener>
// com onXxxEvent(cb) devolvendo a função de cancelamento — só troca o transporte
// (SignalR + grupos do ConversationHub, não canal único do Chatwoot).

export type ConversationRealtimeStatus = 'idle' | 'connecting' | 'connected' | 'disconnected'
export const realtimeStatus = ref<ConversationRealtimeStatus>('idle')

/**
 * Conexão voltou depois de cair (reconexão automática ou nova conexão depois de fechar de vez). O que chegou
 * durante a queda não foi entregue — quem mostra dados ao vivo rebusca aqui, em vez de depender do agente recarregar.
 */
type RecoveredListener = () => void
const recoveredListeners = new Set<RecoveredListener>()

export function onRealtimeRecovered(cb: RecoveredListener): () => void {
  recoveredListeners.add(cb)
  return () => recoveredListeners.delete(cb)
}

function notifyRecovered() {
  recoveredListeners.forEach(cb => { try { cb() } catch (err) { console.warn('[ConversationRealtime] recovered listener:', err) } })
}

/** Espera entre tentativas: 0 s, 2 s, 5 s, 10 s e depois 30 s pra sempre — nunca desiste (padrão do SignalR para em 4). */
export function realtimeRetryDelay(previousRetryCount: number): number {
  return [0, 2_000, 5_000, 10_000][previousRetryCount] ?? 30_000
}

export interface MessageBroadcast {
  conversationId: string
  messageId: string
  senderType: string
  senderId: string | null
  body: string | null
  mediaUrl: string | null
  mediaMimeType: string | null
  isPrivateNote: boolean
  createdAt: string
  /** "copilot" = enviada sozinha pelo copiloto de IA, em nome do atendente. */
  origin?: string | null
}

/** Evento "StatusMensagem": andamento de uma mensagem nossa no WhatsApp. */
export interface MessageStatusNotification {
  conversationId: string
  messageId: string
  status: 'sent' | 'delivered' | 'read' | 'failed'
  deliveredAt: string | null
  readAt: string | null
  error: string | null
}

export interface ConversationAssignedNotification {
  conversationId: string
  agentId: string
  isManual: boolean
}

export interface InternalMessageNotification {
  messageId: number
  senderId: string
  receiverId: string
  body: string
  createdAt: string
}

/** Aviso em massa entre agentes (evento "NovoBroadcast", só pros destinatários). */
export interface BroadcastNotification {
  broadcastId: string
  senderId: string
  body: string
  mediaUrl: string | null
  createdAt: string
  /** aviso | relatorio (entrega do Relatório de Resolução). */
  kind?: string
}

/** Um destinatário confirmou um aviso meu (evento "BroadcastConfirmado", só pro grupo de quem enviou). */
export interface BroadcastConfirmedNotification { broadcastId: string; agentId: string; confirmedAt: string }
type BroadcastConfirmedListener = (evt: BroadcastConfirmedNotification) => void
const broadcastConfirmedListeners = new Set<BroadcastConfirmedListener>()

export function onBroadcastConfirmed(cb: BroadcastConfirmedListener): () => void {
  broadcastConfirmedListeners.add(cb)
  return () => broadcastConfirmedListeners.delete(cb)
}

/** Contato entrou/saiu da lista negra (evento "ListaNegraAlterada", todos os painéis). */
export interface BlacklistChangedNotification { contactId: string; blocked: boolean }
type BlacklistChangedListener = (evt: BlacklistChangedNotification) => void
const blacklistChangedListeners = new Set<BlacklistChangedListener>()

export function onBlacklistChanged(cb: BlacklistChangedListener): () => void {
  blacklistChangedListeners.add(cb)
  return () => blacklistChangedListeners.delete(cb)
}

/** Contato bloqueado mandou mensagem (evento "MensagemBloqueada", só com blacklist.manage). */
export interface BlockedMessageNotification { contactId: string; contactName: string; messageType: string; body: string | null; receivedAt: string }
type BlockedMessageListener = (evt: BlockedMessageNotification) => void
const blockedMessageListeners = new Set<BlockedMessageListener>()

export function onBlockedMessage(cb: BlockedMessageListener): () => void {
  blockedMessageListeners.add(cb)
  return () => blockedMessageListeners.delete(cb)
}

/** Conversas esperando atendente além do limite (evento "AlertaEsperaSemAgente", grupo "painel" —
 * administradores). Uma vez por conversa (ver AlertWaitingConversationsCommandHandler no backend). */
export interface WaitingAlertNotification {
  count: number
  thresholdMinutes: number
  conversations: Array<{ conversationId: string; contactName: string; teamName: string | null; flowName: string | null; waitingMinutes: number }>
  createdAt: string
}

type WaitingAlertListener = (evt: WaitingAlertNotification) => void
const waitingAlertListeners = new Set<WaitingAlertListener>()

export function onWaitingAlert(cb: WaitingAlertListener): () => void {
  waitingAlertListeners.add(cb)
  return () => waitingAlertListeners.delete(cb)
}

/** O próprio agente leu a conversa em outra aba/dispositivo (evento "ConversaLida", só pro grupo dele). */
export interface ConversationReadNotification {
  conversationId: string
  agentId: string
}

type ConversationReadListener = (evt: ConversationReadNotification) => void
const conversationReadListeners = new Set<ConversationReadListener>()

export function onConversationRead(cb: ConversationReadListener): () => void {
  conversationReadListeners.add(cb)
  return () => conversationReadListeners.delete(cb)
}

type BroadcastListener = (evt: BroadcastNotification) => void
type MessageListener = (evt: MessageBroadcast) => void
type AssignedListener = (evt: ConversationAssignedNotification) => void
type InternalMessageListener = (evt: InternalMessageNotification) => void
type ListChangedListener = (item: ConversationListItem) => void

const messageListeners = new Set<MessageListener>()
const statusListeners = new Set<(evt: MessageStatusNotification) => void>()
const assignedListeners = new Set<AssignedListener>()
const internalMessageListeners = new Set<InternalMessageListener>()
const listChangedListeners = new Set<ListChangedListener>()
const broadcastListeners = new Set<BroadcastListener>()

/** Consumo do servidor ao vivo (evento "ConsumoAtualizado", grupo "monitoramento" — só com monitoring.view). */
type ResourceUsageListener = (evt: import('@/services/wootdashMonitoring').ResourceUsageLive) => void
const resourceUsageListeners = new Set<ResourceUsageListener>()

export function onResourceUsage(cb: ResourceUsageListener): () => void {
  resourceUsageListeners.add(cb)
  return () => resourceUsageListeners.delete(cb)
}

/** Página Métricas: coleta nova das fotos dos agentes (evento "MetricasAtualizadas", grupo "metricas" — só com metricas.view). */
type MetricsUpdatedListener = (evt: { at: string }) => void
const metricsUpdatedListeners = new Set<MetricsUpdatedListener>()

export function onMetricsUpdated(cb: MetricsUpdatedListener): () => void {
  metricsUpdatedListeners.add(cb)
  return () => metricsUpdatedListeners.delete(cb)
}

export function onBroadcast(cb: BroadcastListener): () => void {
  broadcastListeners.add(cb)
  return () => broadcastListeners.delete(cb)
}

/** Tiques das mensagens da conversa — quem chama já entrou no grupo dela (onConversationMessage). */
export function onConversationStatus(conversationId: string, cb: (evt: MessageStatusNotification) => void): () => void {
  const listener = (evt: MessageStatusNotification) => { if (evt.conversationId === conversationId) cb(evt) }
  statusListeners.add(listener)
  return () => statusListeners.delete(listener)
}

export function onNewMessage(cb: MessageListener): () => void {
  messageListeners.add(cb)
  return () => messageListeners.delete(cb)
}

export function onConversationAssigned(cb: AssignedListener): () => void {
  assignedListeners.add(cb)
  return () => assignedListeners.delete(cb)
}

/** Chat entre agentes (WootDash nativo, GET/POST /mensagens-internas) — evento
 * "MensagemInterna", só chega pra quem é o destinatário (grupo "agente:{id}" no
 * ConversationHub, entrada automática no connect). */
export function onInternalMessage(cb: InternalMessageListener): () => void {
  internalMessageListeners.add(cb)
  return () => internalMessageListeners.delete(cb)
}

/** Fase 3 (perf) — o evento agora chega com a conversa já pronta (ConversationListItem),
 * pra quem escuta poder mesclar localmente (upsert/remove) em vez de refazer um GET
 * /conversas completo a cada notificação (ver plano-performance-escalabilidade.md item 3). */
export function onListChanged(cb: ListChangedListener): () => void {
  listChangedListeners.add(cb)
  return () => listChangedListeners.delete(cb)
}

// Conveniência pro ConvDrawer.vue portado (mesmo call-site de onConversationMessage do
// useChatwootMessageRealtime.ts que ele usava antes): assina só as mensagens da conversa
// dada, chamando joinConversation()/leaveConversation() sozinho (o hub só manda NovaMensagem
// pra quem entrou no grupo "conversa:{id}" — ver ConversationHub.cs). A função devolvida
// desfaz os dois (listener + grupo) numa chamada só.
//
// Entrega a mensagem no mesmo formato do GET /conversas/{id}/mensagens (id, não messageId): o chat junta as
// duas fontes pelo id, e com o evento cru a mensagem enviada aparecia duas vezes (a da busca e a do evento).
export function toConversationMessage(evt: MessageBroadcast): ConversationMessage {
  return {
    id: evt.messageId,
    senderType: evt.senderType as ConversationMessage['senderType'],
    senderId: evt.senderId,
    body: evt.body,
    mediaUrl: evt.mediaUrl,
    mediaMimeType: evt.mediaMimeType,
    isPrivateNote: evt.isPrivateNote,
    createdAt: evt.createdAt,
    ...(evt.origin ? { origin: evt.origin } : {}),
  }
}

export function onConversationMessage(conversationId: string, cb: (message: ConversationMessage) => void): () => void {
  joinConversation(conversationId)
  const unsubscribe = onNewMessage(evt => {
    if (evt.conversationId === conversationId) cb(toConversationMessage(evt))
  })
  return () => {
    unsubscribe()
    leaveConversation(conversationId)
  }
}

let connection: signalR.HubConnection | null = null
const joinedConversations = new Set<string>()

// Eventos genéricos do hub, registrados por nome (ex.: Espelho ao vivo) — vale pra conexão atual e pras próximas
// (quem registra antes do login continua recebendo depois que o socket abre).
type HubEventListener = (payload: any) => void
const hubEventListeners = new Map<string, Set<HubEventListener>>()
const attachedHubEvents = new Set<string>()

function attachHubEvent(name: string): void {
  if (!connection || attachedHubEvents.has(name)) return
  attachedHubEvents.add(name)
  connection.on(name, (payload: unknown) => hubEventListeners.get(name)?.forEach(cb => cb(payload)))
}

export function onHubEvent(name: string, cb: HubEventListener): () => void {
  let set = hubEventListeners.get(name)
  if (!set) hubEventListeners.set(name, set = new Set())
  set.add(cb)
  attachHubEvent(name)
  return () => { set!.delete(cb) }
}

/** Chama um método do hub. Rejeita se o socket não está conectado (quem chama decide o que mostrar). */
export function invokeHub<T = unknown>(method: string, ...args: unknown[]): Promise<T> {
  if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
    return Promise.reject(new Error('Sem conexão em tempo real com o servidor.'))
  }
  return connection.invoke<T>(method, ...args)
}

/** Idempotente — chamar de novo com a conexão já aberta não faz nada. */
export function startConversationRealtime(token: string): void {
  if (connection) return
  if (!token) {
    console.warn('[ConversationRealtime] sem token — faça login de novo.')
    return
  }

  realtimeStatus.value = 'connecting'
  connection = new signalR.HubConnectionBuilder()
    // Só WebSocket, sem a ida ao /negotiate: com N réplicas da Api atrás do gateway, o
    // negotiate e a conexão podiam cair em réplicas diferentes (a 2ª não conhece o
    // connectionId da 1ª → falha), o que exigiria sticky session. Indo direto de WebSocket é
    // uma requisição só e qualquer réplica serve — eventos chegam via backplane Redis
    // (gargalos-restantes.md item 10 do WootDash). Custo: sem fallback pra SSE/long
    // polling em rede que bloqueie WebSocket.
    .withUrl(`${WOOTDASH_BASE}/hubs/conversas`, {
      // Reconexão depois da renovação do token usa o mais novo da sessão (api/wootdash.ts).
      accessTokenFactory: () => resolveToken(token),
      skipNegotiation: true,
      transport: signalR.HttpTransportType.WebSockets,
    })
    .withAutomaticReconnect({ nextRetryDelayInMilliseconds: ctx => realtimeRetryDelay(ctx.previousRetryCount) })
    .build()

  attachedHubEvents.clear()
  hubEventListeners.forEach((_, name) => attachHubEvent(name))

  connection.on('NovaMensagem', (evt: MessageBroadcast) => {
    console.debug('[ConversationRealtime] NovaMensagem', evt)
    messageListeners.forEach(cb => cb(evt))
  })
  connection.on('StatusMensagem', (evt: MessageStatusNotification) => {
    statusListeners.forEach(cb => cb(evt))
  })
  connection.on('NovoAtendimentoAtribuido', (evt: ConversationAssignedNotification) => {
    console.debug('[ConversationRealtime] NovoAtendimentoAtribuido', evt)
    assignedListeners.forEach(cb => cb(evt))
  })
  connection.on('ListaDeConversasAlterada', (item: ConversationListItem) => {
    console.debug('[ConversationRealtime] ListaDeConversasAlterada', item)
    listChangedListeners.forEach(cb => cb(item))
  })
  connection.on('ConsumoAtualizado', (evt: Parameters<ResourceUsageListener>[0]) => {
    resourceUsageListeners.forEach(cb => cb(evt))
  })
  connection.on('MetricasAtualizadas', (evt: Parameters<MetricsUpdatedListener>[0]) => {
    metricsUpdatedListeners.forEach(cb => cb(evt))
  })
  connection.on('NovoBroadcast', (evt: BroadcastNotification) => {
    console.debug('[ConversationRealtime] NovoBroadcast', evt)
    broadcastListeners.forEach(cb => cb(evt))
  })
  connection.on('BroadcastConfirmado', (evt: BroadcastConfirmedNotification) => {
    broadcastConfirmedListeners.forEach(cb => cb(evt))
  })
  connection.on('ListaNegraAlterada', (evt: BlacklistChangedNotification) => {
    blacklistChangedListeners.forEach(cb => cb(evt))
  })
  connection.on('MensagemBloqueada', (evt: BlockedMessageNotification) => {
    blockedMessageListeners.forEach(cb => cb(evt))
  })
  connection.on('ConversaLida', (evt: ConversationReadNotification) => {
    conversationReadListeners.forEach(cb => cb(evt))
  })
  connection.on('AlertaEsperaSemAgente', (evt: WaitingAlertNotification) => {
    console.debug('[ConversationRealtime] AlertaEsperaSemAgente', evt)
    waitingAlertListeners.forEach(cb => cb(evt))
  })
  connection.on('SugestaoCopiloto', (evt: CopilotSuggestionNotification) => {
    copilotListeners.forEach(cb => cb(evt))
  })
  connection.on('CopilotoStatus', (evt: CopilotStatusNotification) => {
    copilotStatusListeners.forEach(cb => cb(evt))
  })
  connection.on('MensagemInterna', (evt: InternalMessageNotification) => {
    console.debug('[ConversationRealtime] MensagemInterna', evt)
    internalMessageListeners.forEach(cb => cb(evt))
  })

  connection.onreconnecting(() => { realtimeStatus.value = 'connecting' })
  connection.onreconnected(() => {
    realtimeStatus.value = 'connected'
    rejoinConversations()
    notifyRecovered()
  })
  // Fechou de vez (servidor recusou, rede caiu na primeira conexão…): abre de novo sozinho, com a mesma espera.
  connection.onclose(() => {
    realtimeStatus.value = 'disconnected'
    if (!stopping) scheduleRestart()
  })

  stopping = false
  restartAttempts = 0
  openConnection(false)
}

let stopping = false
let restartAttempts = 0
let restartTimer: ReturnType<typeof setTimeout> | null = null

function rejoinConversations() {
  joinedConversations.forEach(id => connection?.invoke('EntrarNaConversa', id).catch(() => { /* best-effort — reconecta de novo se cair */ }))
}

/** Abre o socket. <paramref name="recovering"/>: já tinha caído antes — avisa quem precisa rebuscar. */
function openConnection(recovering: boolean) {
  const current = connection
  if (!current || current.state !== signalR.HubConnectionState.Disconnected) return
  realtimeStatus.value = 'connecting'
  current.start()
    .then(() => {
      if (connection !== current) return
      realtimeStatus.value = 'connected'
      restartAttempts = 0
      if (recovering) { rejoinConversations(); notifyRecovered() }
    })
    .catch(err => {
      if (connection !== current) return
      console.error('[ConversationRealtime] falha ao conectar:', err)
      realtimeStatus.value = 'disconnected'
      scheduleRestart()
    })
}

function scheduleRestart() {
  if (restartTimer || stopping || !connection) return
  const delay = realtimeRetryDelay(restartAttempts++)
  restartTimer = setTimeout(() => { restartTimer = null; openConnection(true) }, delay)
}

/** Botão "Reconectar" do selo de conexão: tenta agora, sem esperar a próxima volta automática. */
export function reconnectRealtime(): void {
  if (restartTimer) { clearTimeout(restartTimer); restartTimer = null }
  restartAttempts = 0
  openConnection(true)
}

export function stopConversationRealtime(): void {
  stopping = true
  if (restartTimer) { clearTimeout(restartTimer); restartTimer = null }
  connection?.stop()
  connection = null
  attachedHubEvents.clear()
  joinedConversations.clear()
  realtimeStatus.value = 'idle'
}

export function joinConversation(conversationId: string): void {
  joinedConversations.add(conversationId)
  connection?.invoke('EntrarNaConversa', conversationId).catch(() => { /* best-effort */ })
}

export function leaveConversation(conversationId: string): void {
  joinedConversations.delete(conversationId)
  connection?.invoke('SairDaConversa', conversationId).catch(() => { /* best-effort */ })
}

/** Evento "SugestaoCopiloto": o copiloto de IA escreveu uma resposta pra conversa (status pending) ou já enviou
 * sozinho (auto_sent — a mensagem chega pelo NovaMensagem). Chega pra quem entrou no grupo da conversa. */
export interface CopilotSuggestionNotification {
  conversationId: string
  suggestionId: string
  triggerMessageId: string
  agentName: string
  text: string
  safe: boolean
  reason: string | null
  status: 'pending' | 'auto_sent'
  faqQuestion: string | null
  createdAt: string
  /** Quanto dá pra confiar (pela parecença da dúvida/documento usado). */
  confidence?: 'alta' | 'media' | 'baixa'
  /** De onde veio: a dúvida frequente ou o documento da base. */
  sourceTitle?: string | null
  /** Tom em que o atendente pediu pra reescrever. */
  tone?: 'curta' | 'formal' | 'acolhedora' | null
}

/** O que o copiloto está fazendo na conversa: lendo a mensagem do contato, escrevendo, ou não vai sugerir desta vez. */
export interface CopilotStatusNotification { conversationId: string; stage: 'reading' | 'writing' | 'none' }

const copilotStatusListeners = new Set<(evt: CopilotStatusNotification) => void>()

/** Estado ao vivo do copiloto na conversa (evento CopilotoStatus) — a sugestão pronta chega pelo onCopilotSuggestion. */
export function onCopilotStatus(conversationId: string, cb: (evt: CopilotStatusNotification) => void): () => void {
  const listener = (evt: CopilotStatusNotification) => { if (evt.conversationId === conversationId) cb(evt) }
  copilotStatusListeners.add(listener)
  return () => copilotStatusListeners.delete(listener)
}

const copilotListeners = new Set<(evt: CopilotSuggestionNotification) => void>()

/** Sugestões do copiloto pra conversa — quem chama já entrou no grupo dela (onConversationMessage). */
export function onCopilotSuggestion(conversationId: string, cb: (evt: CopilotSuggestionNotification) => void): () => void {
  const listener = (evt: CopilotSuggestionNotification) => { if (evt.conversationId === conversationId) cb(evt) }
  copilotListeners.add(listener)
  return () => copilotListeners.delete(listener)
}
